import { isElementNode, isOneOfTags, isTextNode } from 'anodum';
import getAdvancedSelection from '../../../../text-formatting/accessors/get-advanced-selection';
import getBlockFirstChildElementTag from '../../../../content-editable/helpers/get-block-first-child-element-tag';
import findRootEditableElement from '../../../../content-editable/helpers/find-root-editable-element';
import wrapIntoElement from '../../../../content-editable/helpers/wrap-into-element';
import setCaretInTheEndOfNode from '../../../../content-editable/helpers/set-caret-in-the-end-of-node';
import canBeRichEdited from '../../../../constraints/verifiers/can-be-rich-edited';
import {
  ITALIC_TAG,
  BOLD_TAG,
  STRIKETHROUGH_TAG,
  UNDERLINE_TAG,
  LIST_ELEMENTS,
  LIST_ITEM,
  SUPPORTED_ELEMENTS,
  DEFAULT_BLOCK_TAG,
  BREAK_TAG,
  BLOCK_ELEMENTS,
} from '../../../../content-editable/constants/editable-elements';
import { NBSP_STRING } from '../../../../content-editable/constants/common';
import { BACKSPACE_KEY, DELETE_KEY } from '../../../../content-editable/constants/keys';
import setCaretAtTheBeginningOfNode from '../../../../content-editable/helpers/set-caret-at-the-beginning-of-node';
import isEmptyTextNode from '../../../../content-editable/verifiers/is-empty-text-node';

const preventRootLevelEditing = (event) => {
  const { srcElement, key } = event;
  const isSrcElementNotEditable =
    !canBeRichEdited(srcElement) && !srcElement.hasAttribute('contenteditable');

  if (isSrcElementNotEditable) return;

  const selection = getAdvancedSelection();
  const range = selection.getRangeAt(0);
  const firstElementChildTag = getBlockFirstChildElementTag(srcElement);

  requestAnimationFrame(() => {
    const newSelection = getAdvancedSelection();
    const { anchorNode } = newSelection;

    const closestParentContentEditable = anchorNode.parentNode.closest('[contenteditable]');
    const rootEditable = findRootEditableElement(anchorNode);

    const { ownerDocument: liveDocument } = srcElement;
    const { parentNode, previousElementSibling, nextElementSibling } = anchorNode;

    const wasSelectionColappsed = range.collapsed;
    const hasSiblingElement = Boolean(previousElementSibling || nextElementSibling);
    const rootContainsTextNode = parentNode === srcElement && isTextNode(anchorNode);
    const listRootContainsTextNode =
      isOneOfTags(parentNode, LIST_ELEMENTS) && isTextNode(anchorNode);

    if (rootContainsTextNode && hasSiblingElement && !wasSelectionColappsed) {
      const closestListElement = range.startContainer.parentNode.closest(LIST_ELEMENTS);
      const closestBlockElement =
        closestListElement || range.startContainer.parentNode.closest(BLOCK_ELEMENTS);
      const tagName = closestBlockElement
        ? closestBlockElement.tagName.toLowerCase()
        : DEFAULT_BLOCK_TAG;

      const restoredStartElement = liveDocument.createElement(tagName);
      const textEditableNode = isEmptyTextNode(anchorNode)
        ? liveDocument.createElement(BREAK_TAG)
        : anchorNode.cloneNode();

      if (LIST_ELEMENTS.includes(tagName)) {
        const listItem = liveDocument.createElement(LIST_ITEM);
        const paragraph = liveDocument.createElement(DEFAULT_BLOCK_TAG);

        paragraph.appendChild(textEditableNode);
        listItem.appendChild(paragraph);
        restoredStartElement.appendChild(listItem);
      } else {
        restoredStartElement.appendChild(textEditableNode);
      }

      parentNode.replaceChild(restoredStartElement, anchorNode);
      setCaretInTheEndOfNode(textEditableNode);

      return;
    }

    if (listRootContainsTextNode && hasSiblingElement && !wasSelectionColappsed) {
      const listItem = liveDocument.createElement(LIST_ITEM);
      const paragraph = liveDocument.createElement(DEFAULT_BLOCK_TAG);
      const textEditableNode = isEmptyTextNode(anchorNode)
        ? liveDocument.createElement(BREAK_TAG)
        : anchorNode.cloneNode();

      paragraph.appendChild(textEditableNode);
      listItem.appendChild(paragraph);

      parentNode.replaceChild(listItem, anchorNode);
      setCaretAtTheBeginningOfNode(textEditableNode);

      return;
    }

    if (rootEditable) {
      rootEditable.normalize();
    }

    if (rootEditable && !rootEditable.contains(closestParentContentEditable)) return;

    let targetNode = anchorNode;
    const firstChild = srcElement.firstElementChild;

    if (isElementNode(firstChild)) {
      if (isOneOfTags(firstChild, [ITALIC_TAG, BOLD_TAG, UNDERLINE_TAG, STRIKETHROUGH_TAG])) {
        targetNode = firstChild;
      }
      if (isOneOfTags(firstChild, LIST_ELEMENTS)) {
        let listItem = firstChild.querySelector(LIST_ITEM);
        if (!listItem) {
          listItem = liveDocument.createElement(LIST_ITEM);
          firstChild.appendChild(listItem);
        }
        listItem.appendChild(targetNode);
      }
    }

    if (targetNode === srcElement) {
      // Case when the srcElement's (can be either ws-text or inner child of global)
      // content has been all selected and totally cleared.
      // This makes the srcElement itself to become an empty targetNode.
      // Please check: https://github.com/yola/production/issues/9306
      if (key !== BACKSPACE_KEY && key !== DELETE_KEY) return;

      if (targetNode.innerText.trim()) return;

      const { ownerDocument } = targetNode;
      const spaceTextNode = ownerDocument.createTextNode(NBSP_STRING);
      let newChild = spaceTextNode;

      if (firstElementChildTag) {
        newChild = ownerDocument.createElement(firstElementChildTag);
        newChild.appendChild(spaceTextNode);
      }

      // Currently line breaks are preserved during
      // select all processing, so we need to clear them also
      targetNode.innerHTML = '';
      targetNode.appendChild(newChild);
      setCaretInTheEndOfNode(newChild);
    } else {
      const isFirstElementChildTagSupported = SUPPORTED_ELEMENTS.includes(firstElementChildTag);

      if (isFirstElementChildTagSupported) {
        setCaretInTheEndOfNode(wrapIntoElement(targetNode, firstElementChildTag, true));
      }
    }
  });
};

export default preventRootLevelEditing;
